import * as React from 'react';
import { useEffect } from 'react';
import { Container, Popup } from 'semantic-grab-ui/src';
import { connect } from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import { Link } from 'react-router';
import { Pagination, StatusLabel } from 'components/custom';
import ProgressiveContainer from 'components/container/ProgressiveContainer';
import { countries } from 'data/trident-campaigns';
import TableComponent from './TableComponent';
import TridentCampaignNewSearchForm from './TridentCampaignNewSearchForm';
import { searchTargets } from 'actions/trident-campaigns';
import { getFilterTypes } from 'actions/newTridentExtra';
import { queryStringFromObject } from 'utils/route-utils';
import { globalDefaultSearchParams } from 'config';
import { hasTridentPermission } from 'utils/user-utils';

interface Props {
  // Route props
  router: any;
  location: any;

  // Redux props
  entities: any[];
  countries: any[];
  user: any;
  userPermissions: any;
  getEntitiesRequest: any;
  getFilterTypesRequest: any;
  filterTypes: Record<string, any>;
  searchTargets: (modelName: string, params: any) => void;
  getFilterTypes: () => void;
}

const columns = [
  {
    key: 'name.keyword',
    name: 'Campaign Code',
    renderFunc: (entity: any) => (
      <Link to={`campaigns/${_.get(entity, 'countryID')}/${_.get(entity, 'campaignID')}/edit`}>
        {entity.name}
      </Link>),
  },
  {
    key: 'status',
    renderFunc: (entity: any) => (
      <span>
        <StatusLabel status={entity.status} />
      </span>),
  },
  {
    key: 'countryID',
    name: 'Country',
    renderFunc: (entity: any) => (
      countries[entity.countryID]),
  },
  {
    key: 'startTime',
    renderFunc: (entity: any) => (
      <Popup
        trigger={
          <span>
            {moment(entity.startTime).format('MM/DD/YYYY')}
          </span>
        }
        content={moment(entity.startTime).format('MMMM Do YYYY, h:mm:ss a')}
      />
    ),
  },
  {
    key: 'endTime',
    renderFunc: (entity: any) => (
      <Popup
        trigger={
          <span>
            {moment(entity.endTime).format('MM/DD/YYYY')}
          </span>
        }
        content={moment(entity.endTime).format('MMMM Do YYYY, h:mm:ss a')}
      />
    ),
  },
  {
    key: 'updatedAt',
    name: 'Last Updated',
    renderFunc: (entity: any) => (
      <Popup
        trigger={
          <span>
            {moment(entity.updatedAt).fromNow()}
          </span>
        }
        content={moment(entity.updatedAt).format('MMMM Do YYYY, h:mm:ss a')}
      />
    ),
  },
];

const TridentCampaignSearchView = ({
  router,
  location,
  entities,
  countries,
  user,
  getEntitiesRequest,
  filterTypes,
  searchTargets,
  getFilterTypes,
}: Props) => {
  const searchParams = _.get(location, 'query', {});
  const searchParamsWithDefaults = _.assign({}, searchParams);
  _.defaults(searchParamsWithDefaults, globalDefaultSearchParams);
  const pathname = _.get(location, 'pathname', '');

  // Check if user has permission to approve campaigns
  const hasApproverPermission = hasTridentPermission(user.userPermissions, '', '', 'active');

  useEffect(() => {
    // Initialize filter types
    if (_.get(window.Grab.config, 'CAMPAIGNS_FILTER_FEATURE_ENABLED', true)) {
      getFilterTypes();
    }

    // Load campaigns on mount
    searchCampaigns(searchParamsWithDefaults);
  }, []);

  useEffect(() => {
    // Re-search when URL parameters change
    const searchParamsWithDefaults = _.assign({}, searchParams);
    _.defaults(searchParamsWithDefaults, globalDefaultSearchParams);
    searchCampaigns(searchParamsWithDefaults);
  }, [_.get(location, 'search')]);


  const parseSort = (s: string) => {
    const items = _.split(_.trim(s), ',');
    if (items.length >= 2) {
      return {
        field: items[0],
        asc: items[1] === 'true',
      };
    }
    return {};
  };

  // searchCampaigns constructs query and sends search request
  const searchCampaigns = (searchParams: any) => {
    const { filters, searchText, page, pageSize, country, status, userType, myCampaign, pendingApproval, sort: sortParam } = searchParams;
    let conditions = [];
    if (!_.isEmpty(filters)) {
      conditions = JSON.parse(filters);
    }
    if (!_.isEmpty(searchText)) {
      conditions.push({
        lhs: 'name',
        operator: 'match',
        rhs: searchText,
      });
    }
    const countryID = Number(country);
    if (!_.isNaN(countryID) && countryID > 0) {
      conditions.push({
        lhs: 'countryID',
        operator: 'eq',
        rhs: countryID,
      });
    }
    if (!_.isEmpty(status)) {
      conditions.push({
        lhs: 'status',
        operator: 'eq',
        rhs: status,
      });
    }
    if (!_.isEmpty(userType)) {
      conditions.push({
        lhs: 'targetUserType',
        operator: 'eq',
        rhs: userType,
      });
    }
    if (myCampaign === 'true' || myCampaign === true) {
      conditions.push({
        lhs: 'creator,updater',
        operator: 'multiMatch',
        rhs: user.email,
      });
    }
    if (pendingApproval === 'true' || pendingApproval === true) {
      // Filter by status PENDING
      conditions.push({
        lhs: 'status',
        operator: 'eq',
        rhs: 'PENDING',
      });

      // Filter by approvers containing current user email
      conditions.push({
        lhs: 'approvers',
        operator: 'in',
        rhs: [user.email],
      });
    }

    let pageNum = 1;
    if (!_.isNaN(Number(page)) && Number(page) > 0) {
      pageNum = Number(page);
    }

    let pageSizeNum = 20;
    if (!_.isNaN(Number(pageSize)) && Number(pageSize) > 0) {
      pageSizeNum = Number(pageSize);
    }

    // Sort by startTime ASC when viewing pending approvals, otherwise use default sort
    const sort = (pendingApproval === 'true' || pendingApproval === true)
      ? { field: 'startTime', asc: true }
      : parseSort(sortParam);

    searchTargets('tridentCampaigns', {
      conditions,
      page: pageNum,
      pageSize: pageSizeNum,
      sort,
    });
  };

  const onSort = (key: string) => {
    const sortParam = parseSort(searchParams.sort);
    let asc = false;
    if (key === sortParam.field) {
      asc = !_.get(sortParam, 'asc', false);
    }

    const queryString = queryStringFromObject({
      ...searchParams,
      sort: `${key},${asc}`,
    });
    const path = `${pathname}?${queryString}`;
    router.push(path);
  };

  const handleSearch = (searchData: any) => {
    // Build query parameters
    const queryParam = {
      filters: JSON.stringify(searchData.filters),
      searchText: searchData.searchText,
      country: searchData.country,
      status: searchData.status,
      userType: searchData.userType,
      myCampaign: searchData.myCampaign,
      pendingApproval: searchData.pendingApproval,
    };
    const queryString = queryStringFromObject(queryParam);
    const path = `${pathname}?${queryString}`;

    searchCampaigns(queryParam);
    router.push(path);
  };

  // Prepare table data
  let isLastPage = true;
  if (_.get(searchParamsWithDefaults, 'pageSize')) {
    isLastPage = entities.length < _.get(searchParamsWithDefaults, 'pageSize');
  }

  const sortParam = parseSort(searchParams.sort);
  const tableColumns = _.map(columns, c => {
    let sort = '';
    if (sortParam.field === c.key) {
      sort = sortParam.asc ? 'asc' : 'dsc';
    }
    return {
      ...c,
      sort,
    };
  });

  return (
    <div>
      <ProgressiveContainer
        isLoading={getEntitiesRequest.isPending}
        message="Loading campaigns..."
        style={{ marginTop: '20px' }}
      >
        <TridentCampaignNewSearchForm
          searchParams={searchParams}
          filterTypes={filterTypes}
          countries={countries}
          onSearch={handleSearch}
          hasApproverPermission={hasApproverPermission}
        />

        <TableComponent
          localeSelector="tridentCampaigns.fields"
          rows={entities}
          columns={tableColumns}
          onSort={onSort}
        />
        <Container fluid textAlign="center">
          <Pagination
            pathname={pathname}
            query={searchParams}
            router={router}
            lastpage={isLastPage}
          />
        </Container>
      </ProgressiveContainer>
    </div>
  );
};

// MapStateToProps function
export function mapStateToProps(state: any) {
  const userPermissions = state.app.user.userPermissions;
  const countries = state.countries.entities;

  return {
    countries,
    user: state.app.user,
    entities: _.get(state, 'tridentCampaigns.entities', []),
    userPermissions,
    getEntitiesRequest: _.get(state.requests, 'getEntitiesTridentCampaignsRequest', {}),
    getFilterTypesRequest: _.get(state.requests, 'getFilterTypesRequest', {}),
    filterTypes: _.get(state, 'newTridents.filterTypes', {}),
  };
}

export default connect(mapStateToProps, {
  searchTargets,
  getFilterTypes,
})(TridentCampaignSearchView);
